'use client'

import { motion } from 'framer-motion'
import {
  Award,
  BookOpen,
  Calendar,
  CheckCircle,
  Clock,
  Download,
  ExternalLink,
  Mail,
  MapPin,
  MessageCircle,
  Monitor,
  Phone,
  Share2,
  Users,
} from 'lucide-react'

import { Registration } from '../../lib/workshop-api'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import { Card } from '../ui/card'

interface ConfirmationPageProps {
  registration: Registration | null
  onCancel?: () => void
}

export function ConfirmationPage({ registration, onCancel }: ConfirmationPageProps) {
  const getWorkshopDetails = () => {
    if (!registration?.selectedWorkshops?.length) return null
    return registration.selectedWorkshops[0] // Assigned workshop
  }

  const workshopDetails = getWorkshopDetails()

  const handleDownloadCalendar = () => {
    // Implementation for downloading calendar event
    // This would generate an .ics file
    console.log('Download calendar event')
  }

  const handleShareRegistration = () => {
    if (navigator.share) {
      navigator.share({
        title: 'Workshop Registration Confirmed',
        text: `I'm registered for ${workshopDetails?.title}!`,
        url: window.location.href,
      })
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(window.location.href)
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="space-y-6"
    >
      {/* Success Header */}
      <Card className="border-kinetic-primary-container/35 rounded-none border bg-kinetic-surface-container-low p-8 text-center">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: 'spring', stiffness: 300, damping: 25 }}
          className="bg-kinetic-primary-container/15 border-kinetic-primary-container/40 mx-auto mb-6 flex h-20 w-20 items-center justify-center border"
        >
          <CheckCircle className="h-10 w-10 text-kinetic-primary-container" />
        </motion.div>

        <h1 className="mb-2 font-headline text-3xl font-bold uppercase tracking-tight text-white">
          Registration confirmed
        </h1>
        <p className="text-lg text-kinetic-on-surface-variant">
          You&apos;re all set for your workshop. We can&apos;t wait to see you there.
        </p>

        {registration?.id && (
          <div className="border-kinetic-outline-variant/30 mt-4 inline-block border bg-kinetic-surface-container-lowest p-3">
            <div className="font-mono text-[10px] uppercase tracking-wider text-kinetic-on-surface-variant">
              Registration ID:{' '}
              <span className="text-kinetic-primary-container">{registration.id}</span>
            </div>
          </div>
        )}
      </Card>

      {/* Workshop Details */}
      {workshopDetails && (
        <Card className="border-kinetic-outline-variant/30 rounded-none border bg-kinetic-surface-container-low p-6">
          <h2 className="mb-4 font-headline text-xl font-semibold uppercase tracking-tight text-white">
            Workshop details
          </h2>

          <div className="space-y-4">
            <div>
              <h3 className="font-headline text-lg font-medium text-white">
                {workshopDetails.title}
              </h3>
              <div className="mt-2 flex flex-wrap items-center gap-2 space-x-3">
                <Badge className="border-kinetic-outline-variant/40 rounded-none bg-kinetic-surface-container-highest capitalize text-kinetic-on-surface">
                  {workshopDetails.format}
                </Badge>
                <Badge
                  variant="outline"
                  className="border-kinetic-primary-container/50 rounded-none text-kinetic-primary-container"
                >
                  Confirmed
                </Badge>
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              {/* Date & Time */}
              <div className="flex items-start space-x-3">
                <Calendar className="mt-1 h-5 w-5 shrink-0 text-kinetic-primary-container" />
                <div>
                  <div className="font-medium text-kinetic-on-surface">Date &amp; time</div>
                  <div className="text-kinetic-on-surface-variant">
                    {/* Placeholder dates - would come from workshop instance */}
                    March 15-16, 2024
                    <br />
                    9:00 AM - 5:00 PM AEDT
                  </div>
                </div>
              </div>

              {/* Location */}
              <div className="flex items-start space-x-3">
                {workshopDetails.format === 'in-person' ? (
                  <MapPin className="mt-1 h-5 w-5 shrink-0 text-kinetic-primary-container" />
                ) : (
                  <Monitor className="mt-1 h-5 w-5 shrink-0 text-kinetic-primary-container" />
                )}
                <div>
                  <div className="font-medium text-kinetic-on-surface">Location</div>
                  <div className="text-kinetic-on-surface-variant">
                    {workshopDetails.format === 'in-person'
                      ? 'Cloud Shuttle Office, Sydney CBD'
                      : 'Online via Zoom'}
                  </div>
                </div>
              </div>

              {/* Duration */}
              <div className="flex items-start space-x-3">
                <Clock className="mt-1 h-5 w-5 shrink-0 text-kinetic-primary-container" />
                <div>
                  <div className="font-medium text-kinetic-on-surface">Duration</div>
                  <div className="text-kinetic-on-surface-variant">2 days (16 hours total)</div>
                </div>
              </div>

              {/* Capacity */}
              <div className="flex items-start space-x-3">
                <Users className="mt-1 h-5 w-5 shrink-0 text-kinetic-primary-container" />
                <div>
                  <div className="font-medium text-kinetic-on-surface">Class size</div>
                  <div className="text-kinetic-on-surface-variant">
                    Small group (max 12 participants)
                  </div>
                </div>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* What's Next */}
      <Card className="border-kinetic-outline-variant/30 rounded-none border bg-kinetic-surface-container-low p-6">
        <h2 className="mb-4 font-headline text-xl font-semibold uppercase tracking-tight text-white">
          What happens next
        </h2>

        <div className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="border-kinetic-outline-variant/25 border bg-kinetic-surface-container-lowest p-4">
              <div className="mb-3 flex items-center space-x-3">
                <Mail className="h-6 w-6 text-kinetic-primary-container" />
                <h3 className="font-medium text-white">Email confirmation</h3>
              </div>
              <p className="text-sm text-kinetic-on-surface-variant">
                You&apos;ll receive a detailed confirmation email with workshop materials, pre-work
                instructions, and joining details.
              </p>
            </div>

            <div className="border-kinetic-outline-variant/25 border bg-kinetic-surface-container-lowest p-4">
              <div className="mb-3 flex items-center space-x-3">
                <BookOpen className="h-6 w-6 text-kinetic-secondary-fixed" />
                <h3 className="font-medium text-white">Preparation materials</h3>
              </div>
              <p className="text-sm text-kinetic-on-surface-variant">
                Access to pre-workshop materials and setup instructions will be provided 1 week
                before the workshop.
              </p>
            </div>

            <div className="border-kinetic-outline-variant/25 border bg-kinetic-surface-container-lowest p-4">
              <div className="mb-3 flex items-center space-x-3">
                <MessageCircle className="h-6 w-6 text-kinetic-primary-container" />
                <h3 className="font-medium text-white">Community access</h3>
              </div>
              <p className="text-sm text-kinetic-on-surface-variant">
                Join our workshop community for networking, Q&amp;A, and ongoing support from
                instructors and peers.
              </p>
            </div>

            <div className="border-kinetic-outline-variant/25 border bg-kinetic-surface-container-lowest p-4">
              <div className="mb-3 flex items-center space-x-3">
                <Award className="h-6 w-6 text-kinetic-secondary-fixed" />
                <h3 className="font-medium text-white">Certification</h3>
              </div>
              <p className="text-sm text-kinetic-on-surface-variant">
                Receive a certificate of completion and digital badge for your LinkedIn profile upon
                workshop completion.
              </p>
            </div>
          </div>
        </div>
      </Card>

      {/* Quick Actions */}
      <Card className="border-kinetic-outline-variant/30 rounded-none border bg-kinetic-surface-container-low p-6">
        <h2 className="mb-4 font-headline text-xl font-semibold uppercase tracking-tight text-white">
          Quick actions
        </h2>

        <div className="grid gap-4 md:grid-cols-3">
          <Button
            onClick={handleDownloadCalendar}
            variant="outline"
            className="border-kinetic-outline-variant/40 flex h-auto flex-col items-center space-y-2 rounded-none p-4 text-kinetic-on-surface hover:bg-kinetic-surface-container-high"
          >
            <Download className="h-6 w-6 text-kinetic-primary-container" />
            <div className="text-center">
              <div className="font-medium text-white">Add to calendar</div>
              <div className="text-sm text-kinetic-on-surface-variant">Download .ics file</div>
            </div>
          </Button>

          <Button
            onClick={handleShareRegistration}
            variant="outline"
            className="border-kinetic-outline-variant/40 flex h-auto flex-col items-center space-y-2 rounded-none p-4 text-kinetic-on-surface hover:bg-kinetic-surface-container-high"
          >
            <Share2 className="h-6 w-6 text-kinetic-primary-container" />
            <div className="text-center">
              <div className="font-medium text-white">Share</div>
              <div className="text-sm text-kinetic-on-surface-variant">Tell your network</div>
            </div>
          </Button>

          <Button
            variant="outline"
            className="border-kinetic-outline-variant/40 flex h-auto flex-col items-center space-y-2 rounded-none p-4 text-kinetic-on-surface hover:bg-kinetic-surface-container-high"
            asChild
          >
            <a href="mailto:workshops@cloudshuttle.com.au">
              <Mail className="h-6 w-6 text-kinetic-primary-container" />
              <div className="text-center">
                <div className="font-medium text-white">Contact us</div>
                <div className="text-sm text-kinetic-on-surface-variant">Questions?</div>
              </div>
            </a>
          </Button>
        </div>
      </Card>

      {/* Support & Contact */}
      <Card className="border-kinetic-outline-variant/30 rounded-none border bg-kinetic-surface-container-lowest p-6">
        <h2 className="mb-4 font-headline text-xl font-semibold uppercase tracking-tight text-white">
          Need help?
        </h2>

        <div className="space-y-4">
          <p className="text-kinetic-on-surface-variant">
            Our team is here to help make your workshop experience amazing. Don&apos;t hesitate to
            reach out if you have any questions.
          </p>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="flex items-center space-x-3">
              <Mail className="h-5 w-5 shrink-0 text-kinetic-primary-container" />
              <div>
                <div className="font-medium text-white">Email support</div>
                <a
                  href="mailto:workshops@cloudshuttle.com.au"
                  className="text-kinetic-primary-container hover:underline"
                >
                  workshops@cloudshuttle.com.au
                </a>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              <Phone className="h-5 w-5 shrink-0 text-kinetic-primary-container" />
              <div>
                <div className="font-medium text-white">Phone support</div>
                <a
                  href="tel:+61234567890"
                  className="text-kinetic-primary-container hover:underline"
                >
                  +61 2 3456 7890
                </a>
              </div>
            </div>
          </div>

          <div className="border-kinetic-outline-variant/30 border-t pt-4">
            <Button
              variant="outline"
              size="sm"
              className="border-kinetic-outline-variant/40 rounded-none"
              asChild
            >
              <a
                href="/workshops/faq"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center space-x-2"
              >
                <ExternalLink className="h-4 w-4" />
                <span>Workshop FAQ</span>
              </a>
            </Button>
          </div>
        </div>
      </Card>

      {/* Optional Cancel Action */}
      {onCancel && (
        <div className="border-kinetic-outline-variant/30 border-t pt-6 text-center">
          <p className="mb-4 text-kinetic-on-surface-variant">Need to cancel your registration?</p>
          <Button
            onClick={onCancel}
            variant="outline"
            className="border-red-500/50 hover:bg-red-950/30 rounded-none text-red-400"
          >
            Cancel Registration
          </Button>
        </div>
      )}
    </motion.div>
  )
}
